import React, { useEffect, useState } from 'react';
import {
  Table, Button, Popconfirm, message, Card, Space, Select, Tooltip
} from 'antd';
import { DeleteOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';

const { Option } = Select;

const Fingerprints = () => {
  const [data, setData] = useState([]);
  const [serials, setSerials] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  
  const { getAuthHeaders, API_BASE_URL } = useAuth();

  useEffect(() => {
    fetchSerials();
    fetchCustomers();
    fetchData();
  }, [pagination.current, pagination.pageSize, selectedSerial, selectedCustomer]);

  const fetchSerials = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/serials`, {
        headers: getAuthHeaders(),
        params: { per_page: 1000 }
      });
      if (response.data.success) {
        setSerials(response.data.data.items);
      }
    } catch (error) {
      console.error('获取序列号列表失败:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/customers/all`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setCustomers(response.data.data);
      }
    } catch (error) {
      console.error('获取客户列表失败:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize
      };
      if (selectedSerial) {
        params.serial_id = selectedSerial;
      }
      if (selectedCustomer) {
        params.customer_id = selectedCustomer;
      }
      
      const response = await axios.get(`${API_BASE_URL}/fingerprints`, {
        headers: getAuthHeaders(),
        params
      });
      if (response.data.success) {
        setData(response.data.data.items);
        setPagination(prev => ({ ...prev, total: response.data.data.total }));
      }
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/fingerprints/${id}`, {
        headers: getAuthHeaders()
      });
      message.success('删除成功，已释放激活名额');
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.username : null;
  };

  const getSerialNumber = (serialId) => {
    const serial = serials.find(s => s.id === serialId);
    return serial ? serial.serial_number : null;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70
    },
    {
      title: '序列号',
      dataIndex: 'serial_id',
      key: 'serial_id',
      width: 180,
      render: (serialId) => {
        const serialNumber = getSerialNumber(serialId);
        return serialNumber ? <code>{serialNumber}</code> : serialId;
      }
    },
    {
      title: '所属客户',
      dataIndex: 'customer_id',
      key: 'customer_id',
      width: 150,
      render: (customerId, record) => {
        if (record.customer_name) {
          return (
            <Space>
              <UserOutlined />
              <span>{record.customer_name}</span>
            </Space>
          );
        }
        return customerId ? getCustomerName(customerId) || `ID:${customerId}` : '-';
      }
    },
    {
      title: '硬件指纹',
      dataIndex: 'hardware_fingerprint',
      key: 'hardware_fingerprint',
      ellipsis: true,
      render: (text) => <code style={{ fontSize: 12 }}>{text}</code>
    },
    {
      title: '许可证码',
      dataIndex: 'license_code',
      key: 'license_code',
      ellipsis: true,
      render: (text) => <code style={{ fontSize: 10 }}>{text?.substring(0, 40)}...</code>
    },
    {
      title: '激活时间',
      dataIndex: 'activated_at',
      key: 'activated_at',
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-',
      width: 150
    },
    {
      title: '最后验证',
      dataIndex: 'last_verified_at',
      key: 'last_verified_at',
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '从未',
      width: 150
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => isActive ? '有效' : '禁用',
      width: 80
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="确定要删除吗？这将释放一个激活名额。"
          onConfirm={() => handleDelete(record.id)}
        >
          <Button icon={<DeleteOutlined />} size="small" danger />
        </Popconfirm>
      )
    }
  ];

  return (
    <div>
      <Card
        title="硬件指纹管理"
        extra={
          <Space>
            <Select
              placeholder="筛选序列号"
              allowClear
              style={{ width: 200 }}
              onChange={setSelectedSerial}
            >
              {serials.map(s => (
                <Option key={s.id} value={s.id}>{s.serial_number}</Option>
              ))}
            </Select>
            <Select
              placeholder="筛选客户"
              allowClear
              style={{ width: 180 }}
              onChange={setSelectedCustomer}
              showSearch
              optionFilterProp="children"
            >
              {customers.map(c => (
                <Option key={c.id} value={c.id}>
                  {c.username} {c.company_name ? `(${c.company_name})` : ''}
                </Option>
              ))}
            </Select>
            <Tooltip title="刷新">
              <Button icon={<ReloadOutlined />} onClick={fetchData} />
            </Tooltip>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          onChange={setPagination}
          scroll={{ x: 1400 }}
        />
      </Card>
    </div>
  );
};

export default Fingerprints;
